import React, { useCallback, useMemo } from "react";

import {
  faChevronRight,
  faExternalLink,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Container, Image, Row } from "react-bootstrap";
import { NavLink } from "react-router-dom";

import useProjects from "@/hooks/useProjects";
import { displayProjectInfo } from "@/utils/Helper";
import { getCurrentLang, getUrlLangPrefix } from "@/utils/Local";

import i18n from "../i18n";
import ErrorComponent from "./Error.component";
import LoaderComponent from "./Loader.component";

import type { IProject } from "@/interfaces/IProject";

const ProjectsComponent = () => {
  const { t } = i18n;
  const lang = getCurrentLang();
  const langPrefix = getUrlLangPrefix();

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useProjects();

  const projects = useMemo(
    () =>
      data?.pages
        .flatMap((page) => page.projects)
        .filter((project) => project.is_published) ?? [],
    [data?.pages],
  );

  const projectLink = useCallback(
    (project: IProject): string => {
      const title = project.title[lang]
        .replaceAll("?", "")
        .replaceAll(" ", "_");
      return `${langPrefix}/${t("_route.projects")}/${title}?id=${project.id}`;
    },
    [lang, langPrefix, t],
  );

  if (isFetching) {
    return <LoaderComponent standalone={false} />;
  }

  if (error) {
    return <ErrorComponent standalone={false} />;
  }

  if (!data) {
    return <React.Fragment>l</React.Fragment>;
  }

  return (
    <div className="min-vh-50">
      {projects.map((project: IProject, index: number) => {
        const imgContainerOrder =
          index % 2 === 0 ? "order-1 order-lg-1" : "order-1 order-lg-2";
        const contentContainerOrder =
          index % 2 === 0 ? "order-2 order-lg-2" : "order-2 order-lg-1";
        const showProjectLink =
          !project.under_development && project.images.length > 1;
        return (
          <Container key={project.id} className="project-container mt-5 mb-5">
            <Row>
              <Col
                className={`text-center mb-4 mb-lg-0 ${imgContainerOrder}`}
                lg={6}
                xs={12}
              >
                {showProjectLink ? (
                  <NavLink key={`link-${project.id}`} to={projectLink(project)}>
                    <Image
                      rounded
                      alt={project.title[lang]}
                      className="mw-100"
                      src={project.images[0].thumbnail}
                    />
                  </NavLink>
                ) : (
                  <Image
                    rounded
                    alt={project.title[lang]}
                    className="mw-100"
                    src={project.images[0].thumbnail}
                  />
                )}
              </Col>
              <Col className={contentContainerOrder} lg={6} xs={12}>
                <div className="flexContainer" style={{ gap: "8px" }}>
                  <h2>{project.title[lang]}</h2>
                  <h3>{project.location}</h3>
                  <p>{project.description[lang]}</p>
                  {displayProjectInfo(project, t)}
                  <div className="d-flex flex-column gap-2 w-100">
                    {showProjectLink ? (
                      <div className=" w-100">
                        <NavLink to={projectLink(project)}>
                          <Button className="btn-primary w-100">
                            {t("View project")}
                            <FontAwesomeIcon
                              className="ms-2"
                              icon={faChevronRight}
                            />
                          </Button>
                        </NavLink>
                      </div>
                    ) : null}
                    <div className="d-flex flex-column flex-lg-row gap-2 w-100">
                      {project.build_link ? (
                        <a
                          className="w-100"
                          href={project.build_link}
                          rel="noreferrer"
                          target="_blank"
                        >
                          <Button className="btn-outline-primary w-100">
                            {t("Watch how it is built")}
                            <FontAwesomeIcon
                              className="ms-2"
                              icon={faExternalLink}
                            />
                          </Button>
                        </a>
                      ) : null}
                      {project.review_link ? (
                        <a
                          className="w-100"
                          href={project.review_link}
                          rel="noreferrer"
                          target="_blank"
                        >
                          <Button className="btn-outline-primary  w-100">
                            {t("Watch homeowner review")}
                            <FontAwesomeIcon
                              className="ms-2"
                              icon={faExternalLink}
                            />
                          </Button>
                        </a>
                      ) : null}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            <hr />
          </Container>
        );
      })}

      {hasNextPage ? (
        <Row>
          <Col className="text-center mt-4" xs={12}>
            <Button
              className="btn-outline"
              disabled={isFetchingNextPage}
              onClick={async () => fetchNextPage().then(() => {})}
              size="sm"
              variant="outline-success ps-4 pe-4"
            >
              {t("load_more")}
            </Button>
          </Col>
        </Row>
      ) : null}
    </div>
  );
};

export default ProjectsComponent;
